import { api } from './api';

export type BuilderGovernanceEventType = 'builder_preview' | 'builder_publish' | 'builder_rollback';

export type BuilderGovernancePayload = {
  eventType: BuilderGovernanceEventType;
  module: string;
  route?: string;
  artifactType?: string;
  artifactId?: string;
  configVersion?: string;
  artifactGraphVersion?: string;
  summary?: string;
  previewHash?: string;
};

export type BuilderGovernanceInput = {
  eventType: BuilderGovernanceEventType;
  artifactType: string;
  artifact?: Record<string, unknown>;
  artifactId?: string | null;
  name?: string | null;
  description?: string | null;
  route?: string;
  module?: string;
  configVersion?: string;
  artifactGraphVersion?: string;
  summary?: string;
};

export type BuilderGovernanceResult = {
  auditId: string;
};

const ARTIFACT_TYPE_MODULE_MAP: Record<string, string> = {
  'billing-model': 'finance',
  'charge-event': 'finance',
  'report-fact': 'finance',
  'report-model': 'finance',
  'printable-view-model': 'finance',
  'semantic-model': 'wms',
  'view-template': 'wms',
  'workspace-template': 'wms',
  'reaction-rule': 'wms',
  'virtual-endpoint': 'wms',
  bundle: 'wms',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readString(record: Record<string, unknown> | undefined, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

export function inferBuilderGovernanceModule(
  artifactType: string,
  artifact?: Record<string, unknown>,
): string {
  const explicit = readString(artifact, ['module', 'domain', 'packModule']);
  if (explicit) return explicit;

  const menuEntry = isRecord(artifact?.menuEntry) ? artifact.menuEntry : undefined;
  const menuGroup = readString(menuEntry, ['group', 'module', 'domain']);
  if (menuGroup) return menuGroup.replace(/^app-/, '');

  return ARTIFACT_TYPE_MODULE_MAP[artifactType] ?? 'wms';
}

function stableSerialize(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`).join(',')}}`;
}

export function stableBuilderPreviewHash(value: unknown): string {
  const input = stableSerialize(value);
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function buildBuilderGovernancePayload(input: BuilderGovernanceInput): BuilderGovernancePayload {
  const artifactId = input.artifactId
    ?? readString(input.artifact, ['id', 'slug', 'name', 'title'])
    ?? input.name
    ?? `${input.artifactType}:${input.eventType}`;
  const module = input.module ?? inferBuilderGovernanceModule(input.artifactType, input.artifact);
  const previewHash = input.artifact
    ? stableBuilderPreviewHash({
      artifactType: input.artifactType,
      artifact: input.artifact,
      name: input.name ?? null,
    })
    : undefined;

  return {
    eventType: input.eventType,
    module,
    route: input.route ?? '/admin/builder',
    artifactType: input.artifactType,
    artifactId,
    configVersion: input.configVersion ?? `builder:${input.artifactType}:${artifactId}`,
    artifactGraphVersion: input.artifactGraphVersion ?? `builder-artifact-graph:${module}:${input.artifactType}`,
    previewHash,
    summary: input.summary ?? `${input.eventType} for ${input.artifactType} ${artifactId}.`,
  };
}

export async function recordBuilderGovernanceEvent(
  input: BuilderGovernanceInput,
): Promise<BuilderGovernanceResult> {
  return api.post<BuilderGovernanceResult>(
    '/dynamic-runtime/builder/audit',
    buildBuilderGovernancePayload(input),
  );
}
